import { Card } from "@/components/ui/card";
import { FileArrowDown } from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso, opts = { month: "short", day: "numeric", year: "numeric" }) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", opts);
const roundHrs = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2));

const STATUS = {
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  in_transit: { label: "In transit", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800 border-red-300" },
  unpaid: { label: "Unpaid", cls: "bg-muted text-muted-foreground border-border" },
};

function Donut({ regular, overtime, total }) {
  const R = 52;
  const C = 2 * Math.PI * R;
  const reg = total > 0 ? regular / total : 1;
  const ot = total > 0 ? overtime / total : 0;
  const regLen = C * reg;
  const otLen = C * ot;
  return (
    <svg viewBox="0 0 140 140" className="w-40 h-40 -rotate-90">
      <circle cx="70" cy="70" r={R} fill="none" stroke="#eef1f4" strokeWidth="16" />
      {overtime > 0 && (
        <circle cx="70" cy="70" r={R} fill="none" stroke="#f5b912" strokeWidth="16"
          strokeDasharray={`${otLen} ${C - otLen}`} strokeDashoffset={-regLen} strokeLinecap="butt" />
      )}
      <circle cx="70" cy="70" r={R} fill="none" stroke="#12b886" strokeWidth="16"
        strokeDasharray={`${regLen} ${C - regLen}`} strokeDashoffset={0} strokeLinecap="butt" />
    </svg>
  );
}

export function PayReport({ report, onOpen, hidden }) {
  const st = STATUS[report.status] || STATUS.unpaid;
  const open = () => onOpen?.(report.period_start, report.period_end);
  const show = (v) => (hidden ? "••••" : money(v));
  return (
    <Card
      className="rounded-2xl border-2 border-border p-6 shadow-[6px_6px_0_0_rgba(0,0,0,0.08)] cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.12)] focus:outline-none focus:ring-2 focus:ring-primary/40"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
      data-testid={`pay-report-${report.period_start}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-caps text-muted-foreground">Pay Date: {fmtDate(report.period_end)}</div>
          <div className="text-[11px] text-muted-foreground">{fmtDate(report.period_start, { month: "short", day: "numeric" })} – {fmtDate(report.period_end)}</div>
          <div className="font-display font-bold text-3xl tracking-tighter mt-1" data-testid="pay-report-takehome">{show(report.take_home)}</div>
          <div className="text-xs text-muted-foreground">Take Home Pay</div>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 border rounded-full ${st.cls}`} data-testid="pay-report-status">{st.label}</span>
      </div>

      <div className="relative flex items-center justify-center my-2">
        <Donut regular={report.regular_hours} overtime={report.ot_hours} total={report.total_hours} />
        <div className="absolute flex flex-col items-center">
          <span className="font-display font-bold text-3xl leading-none">{roundHrs(report.total_hours)}</span>
          <span className="text-xs text-muted-foreground">Hrs</span>
        </div>
      </div>

      <div className="text-center mb-4">
        <div className="font-display font-bold text-xl tracking-tighter">{show(report.gross_pay)}</div>
        <div className="text-xs text-muted-foreground">Gross Pay</div>
      </div>

      <div className="divide-y divide-border border-t border-border">
        <Row color="#12b886" label="Hours Worked" value={show(report.regular_pay)} sub={`${roundHrs(report.regular_hours)} hrs`} />
        {report.ot_hours > 0 && (
          <Row color="#f5b912" label="Overtime (1.5×)" value={show(report.ot_pay)} sub={`${roundHrs(report.ot_hours)} hrs`} />
        )}
        <Row color="#0a2540" label="Gross Pay" value={show(report.gross_pay)} bold />
      </div>
      <div className="flex items-center gap-1.5 text-xs text-primary font-semibold mt-3 pt-3 border-t border-border" data-testid="pay-report-open">
        <FileArrowDown size={15} /> View pay stub (PDF)
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">1099 contractor · no tax withholding</p>
    </Card>
  );
}

function Row({ color, label, value, sub, bold }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-2.5">
        <span className="w-1 h-6" style={{ background: color }} />
        <div>
          <div className={`text-sm ${bold ? "font-bold" : ""}`}>{label}</div>
          {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
        </div>
      </div>
      <div className={`font-mono text-sm ${bold ? "font-bold" : ""}`}>{value}</div>
    </div>
  );
}
